import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { can, canSeePayments, ROLE_LABELS } from "@/lib/permissions";
import {
  Sun, LayoutDashboard, FolderOpen, Users, Settings,
  LogOut, Receipt, BarChart3, Wallet, ChevronLeft, ChevronRight, Shield, Zap, ContactIcon,
  CheckSquare, Activity, BookUser, Gauge } from "lucide-react";

const navItems = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/app/dashboard", show: (u) => can(u, "page.dashboard") },
  { label: "My Tasks",  icon: CheckSquare,     path: "/app/my-tasks",  show: (u) => can(u, "page.my_tasks") },
  { label: "Projects",  icon: FolderOpen,      path: "/app/projects",  show: (u) => can(u, "projects.view") },
  { label: "Solar Design", icon: Zap,          path: "/app/solar-design", show: (u) => can(u, "page.solar_design") },
  { label: "CRM",        icon: ContactIcon,     path: "/app/crm",       show: (u) => can(u, "crm.view") },
  { label: "Bill Audit", icon: Gauge,           path: "/app/bill-audit", show: (u) => can(u, "page.bill_audit") },
  { label: "Pulse",     icon: Activity,         path: "/app/pulse",     show: (u) => can(u, "page.pulse") },
  { label: "Payments",  icon: Wallet,           path: "/app/payments",  show: (u) => canSeePayments(u) },
  { label: "Reports",   icon: BarChart3,        path: "/app/reports",   show: (u) => can(u, "reports.view") },
  { label: "Masters",   icon: BookUser,         path: "/app/masters",   show: (u) => can(u, "page.masters") },
  { label: "Team",      icon: Users,            path: "/app/team",      show: (u) => can(u, "page.team") },
  { label: "Settings",  icon: Settings,         path: "/app/settings",  show: (u) => can(u, "page.settings") },
  { label: "Billing",   icon: Receipt,          path: "/app/billing",   show: (u) => can(u, "billing.manage") },
];

export default function Sidebar({ collapsed, onToggle }) {
  const { user, workspace, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname.startsWith(path);
  const handleLogout = () => { logout(); navigate("/login"); };
  const roleLabel = ROLE_LABELS[user?.role] || user?.role;

  const visibleNav = navItems.filter(item => item.show(user));

  return (
    <aside
      style={{
        width: collapsed ? "64px" : "240px",
        transition: "width 0.3s cubic-bezier(0.16,1,0.3,1)",
        background: "#FFFFFF",
        borderRight: "1px solid rgba(46,100,68,0.1)",
        flexShrink: 0,
      }}
      className="flex flex-col h-full z-30 relative"
      data-testid="sidebar"
    >
      {/* Logo + Toggle */}
      <div
        style={{ borderBottom: "1px solid rgba(46,100,68,0.08)", height: "64px" }}
        className="flex items-center justify-between px-4 flex-shrink-0"
      >
        {!collapsed && (
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: "#FFB703", boxShadow: "0 0 14px rgba(255,183,3,0.4)" }}
            >
              <Sun size={15} style={{ color: "#1B2B22" }} />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-bold text-sm leading-none tracking-tight" style={{ color: "#1B2B22" }}>SunBiz</div>
              <div className="text-[9px] font-medium tracking-[0.15em] uppercase leading-none mt-1" style={{ color: "#8AA496" }}>
                by Sun Bizcon
              </div>
            </div>
          </div>
        )}
        {collapsed && (
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center mx-auto"
            style={{ background: "#FFB703", boxShadow: "0 0 14px rgba(255,183,3,0.35)" }}
          >
            <Sun size={15} style={{ color: "#1B2B22" }} />
          </div>
        )}
        <button
          onClick={onToggle}
          data-testid="sidebar-toggle"
          style={{
            background: "#F5F8F5",
            border: "1px solid rgba(46,100,68,0.12)",
            borderRadius: "8px",
            color: "#8AA496",
            width: "28px",
            height: "28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            flexShrink: 0,
            marginLeft: collapsed ? "auto" : "8px",
            transition: "color 0.2s ease, background 0.2s ease",
          }}
          onMouseEnter={e => { e.currentTarget.style.color = "#2F7D52"; e.currentTarget.style.background = "#EFF6F1"; }}
          onMouseLeave={e => { e.currentTarget.style.color = "#8AA496"; e.currentTarget.style.background = "#F5F8F5"; }}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      {/* Workspace */}
      {!collapsed && workspace && (
        <div
          style={{ borderBottom: "1px solid rgba(46,100,68,0.08)", padding: "12px 16px" }}
          className="flex-shrink-0"
        >
          <div className="text-[9px] font-semibold tracking-[0.15em] uppercase mb-2" style={{ color: "#8AA496" }}>
            Workspace
          </div>
          {workspace.logo_url && (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${workspace.logo_url}`}
              alt="logo"
              className="h-6 w-auto max-w-[72px] object-contain mb-1.5 rounded"
            />
          )}
          <div className="text-xs font-semibold truncate" style={{ color: "#1B2B22" }}>{workspace.name}</div>
          <div className="text-[10px] mt-0.5 truncate font-mono" style={{ color: "#8AA496" }}>/{workspace.slug}</div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {visibleNav.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: collapsed ? "10px 0" : "9px 10px",
                justifyContent: collapsed ? "center" : "flex-start",
                borderRadius: "9px",
                transition: "background 0.15s ease, color 0.15s ease",
                color: active ? "#2F7D52" : "#5A7264",
                background: active ? "#E6F4EC" : "transparent",
                borderLeft: active && !collapsed ? "2px solid #2F7D52" : "2px solid transparent",
                paddingLeft: !collapsed ? (active ? "8px" : "10px") : undefined,
                textDecoration: "none",
                fontSize: "13px",
                fontWeight: active ? "600" : "500",
              }}
              onMouseEnter={e => {
                if (!active) {
                  e.currentTarget.style.background = "#F0F7F2";
                  e.currentTarget.style.color = "#2F7D52";
                }
              }}
              onMouseLeave={e => {
                if (!active) {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#5A7264";
                }
              }}
            >
              <Icon size={17} style={{ flexShrink: 0 }} />
              {!collapsed && <span className="truncate font-body">{item.label}</span>}
            </Link>
          );
        })}

        {user?.role === "super_admin" && (
          <Link
            to="/superadmin"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: collapsed ? "10px 0" : "9px 10px",
              justifyContent: collapsed ? "center" : "flex-start",
              borderRadius: "9px",
              color: "#7C3AED",
              background: "#F5F3FF",
              border: "1px solid rgba(124,58,237,0.15)",
              textDecoration: "none",
              fontSize: "13px",
              fontWeight: "500",
              transition: "background 0.15s ease, color 0.15s ease",
              marginTop: "8px",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#EDE9FE"; e.currentTarget.style.color = "#6D28D9"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#F5F3FF"; e.currentTarget.style.color = "#7C3AED"; }}
          >
            <Shield size={17} style={{ flexShrink: 0 }} />
            {!collapsed && <span className="font-body">Super Admin</span>}
          </Link>
        )}
      </nav>

      {/* User Profile */}
      <div
        style={{ borderTop: "1px solid rgba(46,100,68,0.08)", padding: "12px" }}
        className="flex-shrink-0"
      >
        <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs"
            style={{ background: "#E6F4EC", color: "#2F7D52", border: "1.5px solid rgba(46,100,68,0.2)" }}
          >
            {user?.full_name?.charAt(0)?.toUpperCase() || "U"}
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold truncate" style={{ color: "#1B2B22" }}>{user?.full_name}</div>
                <div className="text-[10px] mt-0.5 truncate" style={{ color: "#8AA496" }}>{roleLabel}</div>
              </div>
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                title="Logout"
                style={{
                  background: "none",
                  border: "none",
                  color: "#8AA496",
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "6px",
                  transition: "color 0.2s ease, background 0.2s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.color = "#DC2626"; e.currentTarget.style.background = "#FEF2F2"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "#8AA496"; e.currentTarget.style.background = "none"; }}
              >
                <LogOut size={14} />
              </button>
            </>
          )}
        </div>
      </div>
    </aside>
  );
}
